import { Button, Icon, VStack } from "@chakra-ui/react";
import Footer from "_components/Footer";
import { logEvent } from "helpers/analytics";
import React from "react";
import { Helmet } from "react-helmet";
import CheckmarkCircle from "../../assets/checkmark-circle.png";
import HelmetCircle from "../../assets/helmet-circle.png";
import CommunityCircle from "../../assets/community-circle.png";
import CreatePortfolioStep from "../../assets/create-portfolio-step.png";
import DeliverGreatWorkStep from "../../assets/deliver-great-work-step.png";
import GetPaidStep from "../../assets/get-paid-step.png";
import { FaArrowRight } from "react-icons/fa";
import BuiltForSkilledWorkersSection from "_components/skilled-workers-landing-pages/BuiltForSkilledWorkersSection";
import SkilledWorkersFAQs from "_components/skilled-workers-landing-pages/SkilledWorkersFAQs";
import { skilledWorkersFAQs } from "../faqs/constants";
import SkilledWorkersPortfolioView from "_components/skilled-workers-landing-pages/SkilledWorkersPortfolioView";
import SkilledWorkersJobsView from "_components/skilled-workers-landing-pages/SkilledWorkersJobsView";
import SkilledWorkersInvoicingView from "_components/skilled-workers-landing-pages/SkilledWorkersInvoicingView";
import SkilledWorkersCertificationsView from "_components/skilled-workers-landing-pages/SkilledWorkersCertificationsView";
import SkilledWorkersToolFinancingView from "_components/skilled-workers-landing-pages/SkilledWorkersToolFinancingView";
import SkilledWorkersMarquee from "_components/SkilledWorkersMarquee";
import { useQuery } from "../../hooks";
import { ApplicationConfig } from "context/ApplicationConfig";
import { isLeanServicesModeEnabled } from "helpers/lean-services";

const toolsMap: Record<
  string,
  { title: string; component: () => React.JSX.Element }
> = {
  LaborPass: {
    title: "LaborPass",
    component: SkilledWorkersPortfolioView,
  },
  jobs: {
    title: "Jobs",
    component: SkilledWorkersJobsView,
  },
  // invoicing: {
  //   title: "Invoicing",
  //   component: SkilledWorkersInvoicingView,
  // },
  // certifications: {
  //   title: "Certifications",
  //   component: SkilledWorkersCertificationsView,
  // },
  toolFinancing: {
    title: "Tool Financing",
    component: SkilledWorkersToolFinancingView,
  },
};

export function SkilledWorkers() {
  const [activeTool, setActiveTool] = React.useState("LaborPass");
  const { toggles } = React.useContext(ApplicationConfig);
  const isLeanServicesMode = isLeanServicesModeEnabled(toggles);
  const availableToolsMap = React.useMemo(() => {
    if (!isLeanServicesMode) {
      return toolsMap;
    }

    const { toolFinancing, ...leanModeTools } = toolsMap;
    return leanModeTools;
  }, [isLeanServicesMode]);

  React.useEffect(() => {
    if (!availableToolsMap[activeTool]) {
      setActiveTool("LaborPass");
    }
  }, [activeTool, availableToolsMap]);

  const ActiveComponent = availableToolsMap[activeTool]?.component;

  /**
   * Get the referrer from the url using hooks
   */
  const query = useQuery();

  /**
   * Get referrer from pro.laborhack.com
   */
  const referredBy = query.get("referredBy") || undefined;

  const email = query.get("email") || undefined;

  const hasReferredBy = referredBy !== undefined;

  const applicationUrl = `${process.env.REACT_APP_PRO_APPLICATION_URL}${
    hasReferredBy ? `?referredBy=${referredBy}` : ""
  }${
    email
      ? (hasReferredBy ? `&` : `?`) + `src_email=${encodeURIComponent(email)}`
      : ""
  }`;

  const handleCTAClick = () => {
    logEvent("skilled_workers_landing_page_cta_click");

    window.open(applicationUrl, "_blank");
  };
  return (
    <>
      <Helmet>
        <title>
          LaborHack Skilled Workers - Get hired 10x faster and earn more
        </title>
      </Helmet>
      <VStack spacing={0} w={"full"}>
        <section className="grid grid-cols-1 lg:grid-cols-2 gap-0 w-full border-[#FEFEFE] border-t-[1px]">
          <div className="bg-prussianBlue-500 w-full px-3 lg:px-20 py-8 lg:py-16 flex flex-col gap-8 lg:gap-12 border-[#121212] border-r-[1px] border-b-[1px]">
            <div className="flex flex-col gap-4 lg:gap-6">
              <h1 className="w-[70%] lg:w-full text-3xl lg:text-6xl font-bold text-[#FEFEFE] leading-[42px] lg:leading-[72px]">
                Get hired 10x faster and earn more
              </h1>
              <p className="text-lg lg:text-2xl text-[#EAEAEA] w-[90%] lg:w-[85%]">
                Level up your craft, get more jobs, and earn even more all with
                tools designed to make you stand out.
              </p>
            </div>
            <Button
              backgroundColor={"#FEFEFE"}
              color={"#153243"}
              paddingY={{
                base: 6,
                lg: 10,
              }}
              w={"45%"}
              fontSize={{
                base: 15,
                lg: 18,
              }}
              rightIcon={
                <Icon
                  as={FaArrowRight}
                  w={{
                    base: 4,
                    lg: 5,
                  }}
                  h={{
                    base: 4,
                    lg: 5,
                  }}
                  ml={2}
                  className="animate-wiggle"
                />
              }
              onClick={() => handleCTAClick()}
            >
              Join LaborHack
            </Button>
          </div>
          <div className="bg-[#C1DCEB] border-[#121212] border-b-[1px]">
            <SkilledWorkersMarquee />
          </div>
        </section>
        <section className="w-full flex flex-col border-[#BFBFBF] border-b-[1px]">
          <div className="py-10 lg:py-16 flex flex-col gap-8 ">
            <div className="px-3 lg:px-20 pb-10 lg:pb-16 flex flex-col gap-4 w-full items-center justify-center border-[#121212] border-b-[1px]">
              <h2 className="text-xl lg:text-5xl lg:w-[50%] text-[#122A38] font-medium lg:leading-[56px] text-center">
                Getting started is super-easy
              </h2>
            </div>
          </div>
          <div className="w-full py-12 lg:px-20 flex flex-col lg:flex-row gap-12 lg:gap-6">
            <div className="px-3 lg:px-0 pb-8 lg:pb-0 border-[#121212] border-b-[1px] lg:border-b-0 w-full lg:w-1/3 flex flex-col items-start gap-6">
              <div className="w-fit h-fit">
                <img src={CreatePortfolioStep} alt="Create your LaborPass" />
              </div>
              <div className="flex flex-col gap-4">
                <h4 className="text-[16px] lg:text-[24px] font-medium lg:font-semibold text-[#212121]">
                  Create your LaborPass
                </h4>
                <p className="text-[13px] lg:text-[22px] font-normal text-[#464646]">
                  {`Sign up for free, set up your LaborPass, and offer your work to our global audience.`}
                </p>
              </div>
            </div>

            <div className="px-3 lg:px-0 pb-8 lg:pb-0 border-[#121212] border-b-[1px] lg:border-b-0 w-full lg:w-1/3 flex flex-col items-start gap-6">
              <div className="w-fit h-fit">
                <img src={DeliverGreatWorkStep} alt="Deliver great work" />
              </div>
              <div className="flex flex-col gap-4">
                <h4 className="text-[16px] lg:text-[24px] font-medium lg:font-semibold text-[#212121]">
                  Deliver great work
                </h4>
                <p className="text-[13px] lg:text-[22px] font-normal text-[#464646]">
                  Receive project requests, discuss details with clients, and
                  deliver exceptional results.
                </p>
              </div>
            </div>

            <div className="px-3 lg:px-0 w-full lg:w-1/3 flex flex-col items-start gap-6">
              <div className="w-fit h-fit">
                <img src={GetPaidStep} alt="Get paid" />
              </div>
              <div className="flex flex-col gap-4">
                <h4 className="text-[16px] lg:text-[24px] font-medium lg:font-semibold text-[#212121]">
                  Get paid
                </h4>
                <p className="text-[13px] lg:text-[22px] font-normal text-[#464646]">
                  Get paid on time, every time. Payments are processed quickly
                  and securely within hours
                </p>
              </div>
            </div>
          </div>
        </section>
        <section className="flex flex-col gap-0 w-full">
          <div className="px-3 lg:px-20">
            <div className="py-10 lg:py-16 border-[#BFBFBF] border-b-[1px] grid grid-cols-1 lg:grid-cols-2 gap-4">
              <div className="w-full lg:w-[55%] xl:w-[50%]">
                <h3 className="text-xl lg:text-4xl font-medium">
                  Why join{" "}
                  <span className="text-[#ED6E5C] font-[AeonikPro]">
                    LaborHack
                  </span>{" "}
                  as a skilled worker?
                </h3>
              </div>
              <div className="w-full lg:pl-8">
                <p className="w-full text-base lg:text-2xl">
                  LaborHack is an all-in-one place for top-tier, vetted skilled
                  workers to advance their careers and access exclusive
                  opportunities.
                </p>
              </div>
            </div>
          </div>
          <div className="w-full grid grid-cols-1 lg:grid-cols-3 py-8">
            <div className="py-4 lg:py-8 px-3 lg:px-20 border-[#BFBFBF] border-b-[1px] lg:border-b-0 flex flex-col items-start gap-6">
              <div className="w-14 h-14 lg:w-24 lg:h-24 rounded-full">
                <img src={CheckmarkCircle} alt="check mark icon" />
              </div>
              <div className="flex flex-col gap-4">
                <h4 className="text-[14px] lg:text-[24px] font-semibold text-[#212121]">
                  Pro badge
                </h4>
                <p className="text-[13px] lg:text-[22px] font-normal text-[#464646]">
                  Stand out with a verified Pro badge on your profile and
                  services, signifying your vetted status on LaborHack.
                </p>
              </div>
            </div>

            <div className="py-4 lg:py-8 px-3 lg:px-20 border-[#BFBFBF] border-b-[1px] lg:border-b-0 flex flex-col items-start gap-6">
              <div className="w-14 h-14 lg:w-24 lg:h-24 rounded-full">
                <img src={HelmetCircle} alt="helmet icon" />
              </div>
              <div className="flex flex-col gap-4">
                <h4 className="text-[14px] lg:text-[24px] font-semibold text-[#212121]">
                  Global projects
                </h4>
                <p className="text-[13px] lg:text-[22px] font-normal text-[#464646]">
                  Connect with clients around the world looking for talent like
                  yours for one-time or ongoing projects.
                </p>
              </div>
            </div>

            <div className="py-4 lg:py-8 px-3 lg:px-20 flex flex-col items-start gap-6">
              <div className="w-14 h-14 lg:w-24 lg:h-24 rounded-full">
                <img src={CommunityCircle} alt="gear icon" />
              </div>
              <div className="flex flex-col gap-4">
                <h4 className="text-[14px] lg:text-[24px] font-semibold text-[#212121]">
                  Exclusive community
                </h4>
                <p className="text-[13px] lg:text-[22px] font-normal text-[#464646]">
                  Join our exclusive skilled workers community for strategic
                  partnerships, upskilling opportunities, and perks.
                </p>
              </div>
            </div>
          </div>
        </section>
        <section className="bg-[#DAEAF3] w-full flex flex-col gap-6 lg:gap-10 p-3 lg:p-20">
          <h4 className="text-[#071116] font-medium text-[18px] lg:text-4xl">
            All the tools you need to succeed as a skilled worker
          </h4>
          <p className="text-[#080808] text-base lg:text-2xl w-full lg:w-[50%]">
            {`We go beyond just connecting you with jobs. We're here to help you
            build, grow, and thrive with tools designed for success.`}
          </p>
          <div className="flex flex-wrap items-center justify-center lg:grid grid-cols-5 gap-5">
            {Object.keys(availableToolsMap).map((tool) => {
              return (
                <Button
                  key={tool}
                  color={"#153243"}
                  isActive={activeTool === tool}
                  _active={{
                    backgroundColor: "#173241",
                    color: "#FEFEFE",
                  }}
                  paddingY={{
                    base: 3,
                    lg: 8,
                  }}
                  fontSize={{
                    base: 14,
                    lg: 22,
                  }}
                  variant={"outline"}
                  onClick={() => setActiveTool(tool)}
                >
                  {availableToolsMap[tool].title}
                </Button>
              );
            })}
          </div>
          <div>{ActiveComponent && <ActiveComponent />}</div>
        </section>
        <BuiltForSkilledWorkersSection
          sectionText="Built for skilled workers like you"
          ctaText="Join LaborHack"
          handleCTAClick={handleCTAClick}
        />
        <SkilledWorkersFAQs items={skilledWorkersFAQs} />
        <section className="px-6 py-14 lg:py-16 w-full bg-[#FBE2DE] flex flex-col text-center items-center gap-6 lg:gap-10">
          <div className="w-full flex flex-col items-center gap-4 lg:gap-6">
            <h3 className="text-[20px] lg:text-[48px] font-medium text-[#2F1612] lg:leading-[56px]">
              {`Start earning with LaborHack`}
            </h3>
            <p className="text-base lg:text-2xl text-[#2F1612] lg:leading-8 lg:px-12">
              {`Unlike other platforms, there's no sign-up costs, no
              subscriptions.`}
            </p>
          </div>
          <div className="w-full">
            <Button
              backgroundColor={"#FEFEFE"}
              color={"#2F1612"}
              paddingY={{
                base: 6,
                lg: 10,
              }}
              w={{
                base: "60%",
                lg: "25%",
              }}
              paddingX={{
                base: 8,
                lg: 10,
              }}
              fontSize={{
                base: 15,
                lg: 22,
              }}
              rightIcon={
                <Icon
                  as={FaArrowRight}
                  w={{
                    base: 4,
                    lg: 5,
                  }}
                  h={5}
                  ml={2}
                  className="animate-wiggle"
                />
              }
              onClick={() => handleCTAClick()}
            >
              Join LaborHack
            </Button>
          </div>
        </section>
      </VStack>
      <Footer />
    </>
  );
}
